import { useLoaderData } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { ProductDetail } from '@/components/vite/ProductDetail'
import { RelatedProducts } from '@/components/vite/RelatedProducts'
import type { Product } from '@/lib/types'
import { STALE } from '@/lib/query-client'

async function fetchProduct(slug: string): Promise<Product | null> {
  const res = await fetch(`/api/products/${encodeURIComponent(slug)}`)
  if (res.status === 404) return null
  if (!res.ok) throw new Error('Failed to load product')
  const json = await res.json() as { product?: Product } | Product
  // API returns { product: {...} }; handle both shapes for safety
  if ('product' in json && json.product) return json.product
  return json as Product
}

export default function ProductPage() {
  const { slug } = useLoaderData() as { slug: string }
  const { data: product, isLoading, isError } = useQuery({
    queryKey: ['product', slug],
    queryFn: () => fetchProduct(slug),
    staleTime: STALE.PRODUCTS,
  })

  if (isLoading) return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <div className="h-96 rounded-lg border border-gold/20 bg-navy/40 animate-pulse" />
    </div>
  )
  if (isError) return (
    <div className="max-w-6xl mx-auto px-6 py-12 text-center">
      <p className="text-red-400">Could not load product.</p>
    </div>
  )
  if (!product) return (
    <div className="max-w-6xl mx-auto px-6 py-12 text-center">
      <h1 className="text-3xl font-bold mb-4">Product not found</h1>
    </div>
  )
  const desc = product.description ?? `${product.name} — Official Veterans.Pro merchandise.`
  // Crawlers and social scrapers require ABSOLUTE image URLs (a relative
  // /designs/… path is silently dropped by og:image parsers and schema.org).
  const SITE_ORIGIN = 'https://shop.veterans.pro'
  const absUrl = (u: string) => (u.startsWith('http') ? u : `${SITE_ORIGIN}${u}`)
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    ...(product.description ? { description: product.description } : {}),
    sku: product.sku,
    image: product.images.map((img) => absUrl(img.url)),
    brand: { '@type': 'Brand', name: 'Veterans Shop' },
    offers: {
      '@type': 'Offer',
      priceCurrency: 'USD',
      price: (product.price_cents / 100).toFixed(2),
      availability: 'https://schema.org/InStock',
      url: `${SITE_ORIGIN}/product/${product.slug}`,
    },
  }

  // React 19 natively hoists <title>/<meta>/<link> rendered anywhere into
  // <head> (react-helmet-async is unreliable under React 19), so we render
  // document metadata directly. JSON-LD is read by crawlers from the body too.
  const title = `${product.name} — Shop.Veterans`
  return (
    <>
      <title>{title}</title>
      <meta name="description" content={desc} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={desc} />
      {product.images[0] && <meta property="og:image" content={absUrl(product.images[0].url)} />}
      <meta property="og:type" content="product" />
      <meta property="og:url" content={`${SITE_ORIGIN}/product/${product.slug}`} />
      <meta name="twitter:card" content="summary_large_image" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <ProductDetail product={product} />
        <div className="mt-16 pt-12 border-t border-white/10">
          <RelatedProducts product={product} />
        </div>
      </div>
    </>
  )
}
